// ─── Pixel geometry ───────────────────────────────────────────────────────────
/** Euclidean distance between two pixel points. */
export function pixelDistance(a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return Math.sqrt(dx * dx + dy * dy);
}
/** Sum of segment lengths along an ordered list of points (open polyline). */
export function pixelPolylineLength(pts) {
    if (pts.length < 2)
        return 0;
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
        total += pixelDistance(pts[i - 1], pts[i]);
    }
    return total;
}
/**
 * Signed area of a polygon via the shoelace formula.
 * Positive for counter-clockwise winding, negative for clockwise.
 * Returns 0 for degenerate (< 3 points) polygons.
 */
export function pixelPolygonSignedArea(pts) {
    if (pts.length < 3)
        return 0;
    let sum = 0;
    const n = pts.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        sum += pts[i].x * pts[j].y;
        sum -= pts[j].x * pts[i].y;
    }
    return sum / 2;
}
/** Absolute area of a polygon in pixels². */
export function pixelPolygonArea(pts) {
    return Math.abs(pixelPolygonSignedArea(pts));
}
/** Perimeter of a closed polygon in pixels. */
export function pixelPolygonPerimeter(pts) {
    if (pts.length < 2)
        return 0;
    // Close the polygon by adding the closing segment
    return pixelPolylineLength(pts) + pixelDistance(pts[pts.length - 1], pts[0]);
}
// ─── Real-world conversions ───────────────────────────────────────────────────
/** Convert a pixel length to linear feet using the sheet scale. */
export function pixelsToFeet(px, scale) {
    return px / scale.pixelsPerFoot;
}
/** Convert a pixel² area to square feet using the sheet scale. */
export function pixelsSqToSF(pxSq, scale) {
    return pxSq / (scale.pixelsPerFoot * scale.pixelsPerFoot);
}
// ─── Measurement functions ────────────────────────────────────────────────────
/** Length of an open polyline in linear feet. */
export function polylineLF(pts, scale) {
    return pixelsToFeet(pixelPolylineLength(pts), scale);
}
/** Perimeter of a closed polygon in linear feet. */
export function polygonPerimeterLF(pts, scale) {
    return pixelsToFeet(pixelPolygonPerimeter(pts), scale);
}
/** Area of a closed polygon in square feet. */
export function polygonSF(pts, scale) {
    return pixelsSqToSF(pixelPolygonArea(pts), scale);
}
// ─── Unit conversions ─────────────────────────────────────────────────────────
export const SF_PER_SY = 9;
export const SF_PER_ACRE = 43_560;
export const LF_PER_LY = 3;
export const CF_PER_CY = 27;
export function sfToSY(sf) { return sf / SF_PER_SY; }
export function sfToAcres(sf) { return sf / SF_PER_ACRE; }
export function lfToLY(lf) { return lf / LF_PER_LY; }
export function cfToCY(cf) { return cf / CF_PER_CY; }
/**
 * Cubic yards from a surface area (SF) and a depth in inches.
 * Common for concrete slabs, earthwork cut/fill, gravel base.
 */
export function cyFromSFAndDepthInches(sf, depthInches) {
    const cf = sf * (depthInches / 12);
    return cfToCY(cf);
}
/**
 * Cubic yards from a surface area (SF) and a depth in feet.
 */
export function cyFromSFAndDepthFeet(sf, depthFeet) {
    return cfToCY(sf * depthFeet);
}
// ─── Deduction / exclusion ────────────────────────────────────────────────────
/**
 * Net area after subtracting one or more exclusion polygons.
 * All polygons must be in pixel coordinates; scale is applied to the net result.
 * Exclusion areas that fall outside the outer polygon still count as subtractions
 * (caller is responsible for geometric validity).
 */
export function netPolygonSF(outer, exclusions, scale) {
    const outerPx = pixelPolygonArea(outer);
    const excludePx = exclusions.reduce((acc, e) => acc + pixelPolygonArea(e), 0);
    const netPx = Math.max(0, outerPx - excludePx);
    return pixelsSqToSF(netPx, scale);
}
// ─── Rounding helpers ─────────────────────────────────────────────────────────
/** Round to a given number of decimal places. */
export function round(value, decimals = 2) {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
}
